import { useEffect, useRef } from "react";
import { flushPendingEvents } from "~/lib/gtm-datalayer";

interface IubendaConfiguration {
  siteId: number;
  cookiePolicyId: number;
  lang: string;
  banner: {
    position: string;
    acceptButtonDisplay: boolean;
    rejectButtonDisplay: boolean;
    customizeButtonDisplay: boolean;
  };
  callback?: {
    onConsentGiven?: () => void;
    onConsentRejected?: () => void;
  };
}

declare global {
  interface Window {
    _iub: {
      csConfiguration: IubendaConfiguration;
      csApi?: {
        getConsent: () => boolean;
      };
    };
    dataLayer: Record<string, any>[];
    gtag: (...args: any[]) => void;
  }
}

function updateConsentMode(granted: boolean) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  const state = granted ? "granted" : "denied";
  window.gtag("consent", "update", {
    ad_storage: state,
    ad_user_data: state,
    ad_personalization: state,
    analytics_storage: state,
  });
  if (granted) {
    flushPendingEvents();
  }
}

export function CookieBanner() {
  const scriptLoadedRef = useRef(false);

  useEffect(() => {
    const hasConsent = (): boolean => {
      try {
        const consent = localStorage.getItem("_iub_cs-64318772");
        if (consent) {
          const parsed = JSON.parse(consent);
          return parsed.consent === true || parsed.purposes?.length > 0;
        }
      } catch {
        return false;
      }
      return false;
    };

    if (hasConsent()) {
      updateConsentMode(true);
      return;
    }

    if (scriptLoadedRef.current) {
      return;
    }

    if (document.querySelector('script[src*="iubenda_cs.js"]')) {
      scriptLoadedRef.current = true;
      return;
    }

    window._iub = window._iub || {};
    window._iub.csConfiguration = {
      siteId: 64318772,
      cookiePolicyId: 64318772,
      lang: "it",
      banner: {
        position: "bottom",
        acceptButtonDisplay: true,
        rejectButtonDisplay: true,
        customizeButtonDisplay: true,
      },
      callback: {
        onConsentGiven: () => updateConsentMode(true),
        onConsentRejected: () => updateConsentMode(false),
      },
    };

    const overrideBannerElements = () => {
      const banner = document.querySelector(
        '[id^="iubenda-cs-banner"]'
      ) as HTMLElement;
      if (!banner) return;

      banner.style.position = "fixed";
      banner.style.bottom = "0";
      banner.style.top = "auto";
      banner.style.left = "0";
      banner.style.right = "0";
      banner.style.zIndex = "9999";

      const btnContainer = document.querySelector(
        '[id^="iubenda-cs-banner-buttons"]'
      ) as HTMLElement;
      if (btnContainer) {
        btnContainer.style.display = "flex";
        btnContainer.style.justifyContent = "center";
        btnContainer.style.gap = "0.5rem";
        btnContainer.style.marginTop = "1rem";
      }

      banner.querySelectorAll("button").forEach((btn: HTMLElement) => {
        btn.style.display = "inline-block";
        btn.style.visibility = "visible";
        btn.style.opacity = "1";
      });
    };

    const observer = new MutationObserver((mutations, obs) => {
      if (document.querySelector('[id^="iubenda-cs-banner"]')) {
        overrideBannerElements();
        obs.disconnect();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    const script = document.createElement("script");
    script.src = "https://cdn.iubenda.com/cookie_solution/iubenda_cs.js";
    script.async = true;
    script.onload = () => {
      scriptLoadedRef.current = true;
    };
    document.body.appendChild(script);

    setTimeout(overrideBannerElements, 2000);

    return () => {
      observer.disconnect();
    };
  }, []);

  return null;
}
